export type AdminRole = 'super_admin' | 'mediator' | 'content_editor' | 'support_agent';

export const ROLE_PERMITTED_VIEWS: Record<AdminRole, string[]> = {
  super_admin: [
    'dashboard',
    'users',
    'deleted_accounts',
    'escrow',
    'vouchers',
    'disputes',
    'listings',
    'cms',
    'broadcast',
    'support',
    'audit',
    'admin_team',
    'admin_chat',
    'email_templates',
    'profile_settings'
  ],
  mediator: [
    'dashboard',
    'disputes',
    'escrow',
    'support',
    'admin_chat',
    'profile_settings'
  ],
  content_editor: [
    'dashboard',
    'listings',
    'cms',
    'broadcast',
    'admin_chat',
    'profile_settings'
  ],
  support_agent: [
    'dashboard',
    'support',
    'users',
    'admin_chat',
    'profile_settings'
  ]
};

export const ROLE_DISPLAY_NAMES: Record<AdminRole, string> = {
  super_admin: 'Super Admin',
  mediator: 'Dispute Mediator',
  content_editor: 'Content Editor',
  support_agent: 'Support Agent'
};

export interface AdminUser {
  uid: string;
  email: string;
  displayName: string;
  role: AdminRole;
  isSuperAdmin: boolean;
  avatarUrl?: string;
}

export type VendorTier = 'tier1_bronze' | 'tier2_silver' | 'tier3_champion';

export interface UserReportIncident {
  id: string;
  reporterId: string;
  reporterName: string;
  reportedRole: 'vendor' | 'buyer';
  reason: string;
  description: string;
  timestamp: string;
  relatedListingId?: string;
  status: 'pending' | 'reviewed' | 'actioned';
}

export interface UserBanInfo {
  type: 'temporary' | 'permanent';
  reasonCategory: string;
  staffNotes: string;
  bannedAt: string;
  bannedBy: string;
  expiresAt?: string;
}

export interface UserAppealInfo {
  appealId: string;
  submittedAt: string;
  reason: string;
  userStatement: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewedAt?: string;
  reviewedBy?: string;
  resolutionNotes?: string;
}

export interface UserRecord {
  uid: string;
  name: string;
  email: string;
  phone?: string;
  avatarUrl?: string;
  role: 'buyer' | 'vendor' | 'both';
  status: 'active' | 'suspended' | 'banned' | 'temporarily_banned' | 'permanently_banned';
  tier: VendorTier;
  sellerTrustScore: number;
  walletBalance: number;
  carbonOffsetKg: number;
  tradesCompleted: number;
  activeListingsCount?: number;
  ordersCount?: number;
  totalSpent?: number;
  totalSalesVolume?: number;
  kycStatus: 'unverified' | 'pending' | 'verified' | 'rejected';
  reportCount?: number;
  reports?: UserReportIncident[];
  banInfo?: UserBanInfo;
  appealInfo?: UserAppealInfo;
  createdAt?: any;
  updatedAt?: any;
}

export interface KYCSubmission {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  docType: 'nin' | 'drivers_license' | 'passport' | 'voters_card' | 'cac' | string;
  docNumber: string;
  documentUrl?: string;
  businessName?: string;
  bankName?: string;
  accountNumber?: string;
  accountName?: string;
  residentialAddress?: string;
  proofDocumentUrl?: string;
  submittedAt: any;
  status: 'pending' | 'approved' | 'rejected';
  reviewNotes?: string;
  reviewedBy?: string;
  reviewedAt?: any;
}

export interface DeletedAccountRecord {
  id: string;
  originalUid: string;
  name: string;
  email: string;
  phone?: string;
  registeredAt?: any;
  deletedAt: any;
  deactivationReason: string;
  lastWalletBalance: number;
  tradesCompleted: number;
  transactionsHistory?: Array<{
    id: string;
    type: string;
    amount: number;
    description: string;
    createdAt: any;
    status: string;
  }>;
}

export type EscrowStatus = 'held' | 'dispatched' | 'delivered' | 'disputed' | 'settled' | 'refunded';

export interface EscrowOrder {
  id: string;
  orderNumber: string;
  buyerId: string;
  buyerName: string;
  buyerPhone?: string;
  sellerId: string;
  sellerName: string;
  sellerPhone?: string;
  listingId: string;
  itemTitle: string;
  itemPrice: number;
  escrowBaseFee: number; // min ₦500
  platformFee: number; // 7.5%
  vatOnFee: number; // 7.5% on 7.5% = 0.5625%
  totalDeduction: number; // 8.0625%
  netVendorPayout: number;
  status: EscrowStatus;
  disputeActive: boolean;
  courier?: string;
  trackingCode?: string;
  riderPhone?: string;
  deliveryAddress?: string;
  createdAt: any;
  dispatchedAt?: any;
  deliveredAt?: any;
  settledAt?: any;
  autoReleased?: boolean;
}

export interface WebhookRecord {
  id: string;
  txRef: string;
  flwId?: string;
  userId: string;
  customerEmail?: string;
  amount: number;
  status: 'successful' | 'failed' | 'pending';
  channel: string;
  processedAt: any;
  verified: boolean;
}

export interface AdSubscription {
  id: string;
  vendorId: string;
  vendorName: string;
  listingId: string;
  listingTitle: string;
  amountPaid: number;
  durationDays: number;
  startDate: any;
  endDate: any;
  status: 'active' | 'expired' | 'cancelled';
}

export type DisputeStatus = 'open' | 'under_review' | 'resolved_buyer' | 'resolved_vendor' | 'split';

export interface ChatSnippet {
  id: string;
  senderId: string;
  senderName: string;
  message: string;
  timestamp: any;
  isBuyer: boolean;
}

export interface DisputeCase {
  id: string;
  orderId: string;
  orderNumber: string;
  buyerId: string;
  buyerName: string;
  sellerId: string;
  sellerName: string;
  disputedAmount: number;
  reason: string;
  description: string;
  buyerEvidenceUrls: string[];
  sellerRebuttal?: string;
  sellerEvidenceUrls?: string[];
  status: DisputeStatus;
  resolutionType?: 'full_refund' | 'full_payout' | 'split_payout';
  buyerSplitAmount?: number;
  sellerSplitAmount?: number;
  mediatorNotes?: string;
  mediatorId?: string;
  createdAt: any;
  resolvedAt?: any;
  chatTranscript?: ChatSnippet[];
}

export interface CatalogListing {
  id: string;
  title: string;
  description: string;
  category: string;
  price: number;
  condition: 'Brand New' | 'Like New' | 'Good' | 'Fair';
  ecoScore: number;
  co2SavingsKg: number;
  sellerId: string;
  sellerName: string;
  sellerRating?: number;
  imageUrls: string[];
  status: 'active' | 'flagged' | 'delisted' | 'sold';
  flagReason?: string;
  delistedReason?: string;
  delistedAt?: string;
  aiModerationStatus?: 'clean' | 'flagged' | 'quarantined';
  aiConfidenceScore?: number;
  isFeatured?: boolean;
  location: string;
  createdAt: any;
}

export interface CMSArticle {
  id: string;
  title: string;
  slug: string;
  category: string;
  summary: string;
  content: string;
  coverImage?: string;
  tags: string[];
  status: 'draft' | 'published' | 'scheduled';
  author: string;
  eventDate?: string;
  eventLocation?: string;
  publishedAt?: any;
  createdAt?: any;
}

export type BroadcastTarget = 'all' | 'buyers' | 'vendors' | 'lagos' | 'abuja' | 'portharcourt' | 'kano';

export interface BroadcastNotification {
  id: string;
  title: string;
  body: string;
  deepLink?: string;
  imageUrl?: string;
  targetAudience: BroadcastTarget;
  recipientCount: number;
  status: 'sent' | 'scheduled' | 'failed';
  sentBy: string;
  sentAt: any;
}

export interface SupportTicket {
  id: string;
  ticketNumber: string;
  userId: string;
  userName: string;
  userEmail: string;
  category: 'payment' | 'escrow' | 'delivery' | 'account' | 'bug' | 'other';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  subject: string;
  message: string;
  assignedAgent?: string;
  agentNotes?: string;
  userReplies?: Array<{
    id: string;
    sender: 'user' | 'agent';
    senderName: string;
    senderAvatar?: string;
    senderRole?: string;
    message: string;
    timestamp: any;
  }>;
  createdAt: any;
  updatedAt: any;
}

export interface SecurityAuditLog {
  id: string;
  adminUid: string;
  adminEmail: string;
  adminRole: AdminRole;
  action: string;
  entityType: 'order' | 'user' | 'dispute' | 'listing' | 'deleted_account' | 'broadcast' | 'cms' | 'kyc';
  entityId: string;
  details: string;
  ipAddress?: string;
  timestamp: any;
}

export interface DashboardMetrics {
  gmvLifetime: number;
  gmv30d: number;
  escrowInHold: number;
  platformRevenue: number;
  activeOrders: number;
  co2SavingsTotalKg: number;
  totalUsers: number;
  totalVendors: number;
  totalBuyers: number;
  disputesPending: number;
  kycPending: number;
}

export type DatePreset = 'today' | '7d' | '30d' | '90d' | 'ytd' | 'custom';

export interface PostCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  color: string;
  articleCount: number;
  createdAt?: any;
}

export interface AdminMember {
  uid: string;
  email: string;
  displayName: string;
  name?: string;
  role: AdminRole;
  isSuperAdmin: boolean;
  twoFactorEnabled: boolean;
  status: 'active' | 'suspended';
  lastLoginAt?: any;
  createdAt: any;
  tempPassword?: string;
  avatarUrl?: string;
}

export interface MarketplaceCategory {
  id: string;
  name: string;
  slug: string;
  icon: string;
  imageUrl?: string;
  itemCount: number;
  isFeaturedOnHome: boolean;
  isActive: boolean;
  description?: string;
  createdAt?: any;
}

export interface GovernanceConfig {
  baseEscrowFeeMin: number;
  tierLowPercent: number;
  tierHighPercent: number;
  platformFeePercent: number;
  vatOnPlatformFeePercent: number;
  minSupportedAppVersion: string;
  maintenanceMode: boolean;
  maintenanceMessage: string;
  flutterwaveFloatBalance: number;
  flutterwaveFloatThreshold: number;
  // Viral Referral Program
  referralRewardNgn: number;
  refereeDiscountNgn: number;
  referralEcoPoints: number;
  referralProgramActive: boolean;
  // Automated AI Content Moderation
  aiContentModerationEnabled: boolean;
  aiConfidenceQuarantineThreshold: number;
  autoFlagReplicaKeywords: boolean;
  // Treasury DLQ & Alerts
  lowBalanceAlertPhone: string;
  lowBalanceAlertEmail: string;
  failedPayoutCount: number;
  // Fraud Velocity Limits
  maxListingsPerHourPerUser: number;
  bankChangeWithdrawalFreezeHours: number;
  geofenceSpoofingCheckEnabled: boolean;
}

